import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Heading,
  Text,
  useToast,
  Textarea,
  Divider,
  Badge,
} from "@chakra-ui/react";
import { supabase } from "./utils/supabaseClient";
import { COLORS } from "./styles/colors";
import { ssgApiClient } from "./utils/ssgApiClient";

const SSGAPI = () => {
  // Initialize the API client with Supabase
  useEffect(() => {
    ssgApiClient.setSupabaseClient(supabase);
  }, []);

  const [formType, setFormType] = useState("createClaim");
  const [claimFormData, setClaimFormData] = useState({
    courseRunId: "TGS-2025053785",
    courseFee: "1050",
    startDate: "2025-08-30",
    nric: "S9775747Z",
    email: "jianxiang.liao@sozcode.com",
    mobileNumber: "93889604",
  });

  const [grantFormData, setGrantFormData] = useState({
    course: {
      referenceNumber: "TGS-2020002010",
      startDate: "2025-08-30"
    },
    trainee: {
      idType: "NRIC",
      id: "S8348637F",
      dateOfBirth: "1983-08-28",
      sponsoringEmployerUen: ""
    }
  });

  const [loading, setLoading] = useState(false);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const toast = useToast();

  const handleClaimFormChange = (e) => {
    const { name, value } = e.target;
    setClaimFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGrantFormChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split('.');
    setGrantFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setApiResponse(null);

    try {
      let requestData;
      if (formType === "createClaim") {
        requestData = {
          claimRequest: {
            course: {
              id: String(claimFormData.courseRunId),
              fee: String(claimFormData.courseFee),
              runId: "",
              startDate: String(claimFormData.startDate),
            },
            individual: {
              nric: String(claimFormData.nric),
              email: String(claimFormData.email),
              homeNumber: String(claimFormData.mobileNumber),
              mobileNumber: String(claimFormData.mobileNumber),
            },
            additionalInformation: ""
          }
        };
      } else {
        requestData = grantFormData;
      }
      
      // Show the form data in preview
      setJsonPreview(requestData);

      console.log('Sending request to Edge Function:', {
        formType,
        requestData
      });

      // Send to Edge Function for encryption and API call
      const { data, error } = await supabase.functions.invoke("ssg-api", {
        body: { 
          requestData,
          formType: formType === "createClaim" ? "claim" : "grantCalculator"
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      // Store the API response
      if (data) {
        console.log('Full response:', data);
        setApiResponse(data);
        
        if (data.status >= 400) {
          toast({
            title: "API Error",
            description: `Status: ${data.status} ${data.statusText}\nError: ${data.data?.error || 'Unknown error'}`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Success",
            description: "Request processed successfully",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error details:', error);
      toast({
        title: "Error",
        description: error.message || "An error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const testDirectAPI = async () => {
    setLoading(true);
    setApiResponse(null);
    
    try {
      // Use the secure API client instead of direct calls
      const encryptedData = "tMS1UbMNgBYwa9gR+FrlNpJGkyKNtDnjgr0Uy1SnVnoQy/9MVfLK8Xd3DYbLkhTX8dcr0cnts2h/1sd1Z/v3aEPpAz7nkiu2tYQyeXIbeVcuJXicNe2iCqnswK4NRHR/DF/HeJSJG7Cen3oz9SJa8TXnGGMXXSClg2ESOqFV1/uXjhJAgiWgUzfF6kkaeLEHNyc6Q9ZttcVL9SDjNP8xEhTgNjTuJIJHtKChtWd3USJnLT7tMZcxtqxT3QZFCv/UsC82AcCxphPcmESxtcG6/tK8eR9Oqk58NAnEQXS0l01hXRF4ol5c5pbJpYLWRQetaew2pRhZxX+a3V6G78JHMDKmKs/tkWEm3lEWGoK5KTx3389ANdyZf1baq1V3/sac94sGDOVLagoyeb62P6TsmwLO90hhkS4rTPiTikCVGg18Dj4yfFRwUdn5nf+Pm8mhS12DYPutZMKCFKOobxc4ooWPi7ErpccdDulToQ+MUGwesJ6S95Y7R5avOfL1F5bRkEg8WX05DSCIpwXWtXba1g==";
      
      console.log('Making secure API request using ssgApiClient');
      
      const response = await ssgApiClient.encryptRequest(encryptedData);
      
      setApiResponse(response);

      toast({
        title: "Secure API Call Completed",
        description: `Status: ${response.status} ${response.statusText}`,
        status: response.status >= 200 && response.status < 300 ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Secure API call error:', error);
      setApiResponse({
        error: error.message,
        status: 0,
        statusText: "Network Error"
      });
      
      toast({
        title: "API Call Failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      w={"100%"} 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      bg={COLORS.isabelline} 
      minH="100vh" 
      py={8}
      px={{ base: 4, md: 8 }}
    >
      <Box 
        width="100%" 
        bg="white" 
        maxW={{ base: "100%", md: "800px" }} 
        px={{ base: 4, md: 8 }} 
        py={{ base: 6, md: 8 }} 
        borderRadius="lg" 
        boxShadow="md"
      >
        <Heading 
          size={{ base: "md", md: "lg" }}
          mb={{ base: 6, md: 8 }} 
          color={COLORS.maastrichtBlue} 
          borderBottom="2px solid" 
          borderColor={COLORS.mandarin} 
          pb={2} 
          width="fit-content"
        >
          SSG API Integration
        </Heading>

        <VStack spacing={8} align="stretch">
          <FormControl>
            <FormLabel color={COLORS.maastrichtBlue}>Select Form Type</FormLabel>
            <Select
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              borderColor={COLORS.maastrichtBlue}
            >
              <option value="createClaim">Create Claim Form</option>
              <option value="grantCalculator">Grant Calculator (Personalised)</option>
            </Select>
          </FormControl>

          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              {formType === "createClaim" ? (
                <>
                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Course Run ID</FormLabel>
                    <Input
                      name="courseRunId"
                      value={claimFormData.courseRunId}
                      onChange={handleClaimFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Course Fee</FormLabel>
                    <Input
                      name="courseFee"
                      value={claimFormData.courseFee}
                      onChange={handleClaimFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Start Date</FormLabel>
                    <Input
                      name="startDate"
                      type="date"
                      value={claimFormData.startDate}
                      onChange={handleClaimFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>NRIC</FormLabel>
                    <Input
                      name="nric"
                      value={claimFormData.nric}
                      onChange={handleClaimFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={claimFormData.email}
                      onChange={handleClaimFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Mobile Number</FormLabel>
                    <Input
                      name="mobileNumber"
                      value={claimFormData.mobileNumber}
                      onChange={handleClaimFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>
                </>
              ) : (
                <>
                  <Heading size="md" color={COLORS.maastrichtBlue}>Course Information</Heading>
                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Reference Number</FormLabel>
                    <Input
                      name="course.referenceNumber"
                      value={grantFormData.course.referenceNumber}
                      onChange={handleGrantFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Start Date</FormLabel>
                    <Input
                      name="course.startDate"
                      type="date"
                      value={grantFormData.course.startDate}
                      onChange={handleGrantFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <Heading size="md" color={COLORS.maastrichtBlue}>Trainee Information</Heading>
                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>ID Type</FormLabel>
                    <Select
                      name="trainee.idType"
                      value={grantFormData.trainee.idType}
                      onChange={handleGrantFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    >
                      <option value="NRIC">NRIC</option>
                      <option value="FIN">FIN</option>
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>ID</FormLabel>
                    <Input
                      name="trainee.id"
                      value={grantFormData.trainee.id}
                      onChange={handleGrantFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={COLORS.maastrichtBlue}>Date of Birth</FormLabel>
                    <Input
                      name="trainee.dateOfBirth"
                      type="date"
                      value={grantFormData.trainee.dateOfBirth}
                      onChange={handleGrantFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={COLORS.maastrichtBlue}>Sponsoring Employer UEN</FormLabel>
                    <Input
                      name="trainee.sponsoringEmployerUen"
                      value={grantFormData.trainee.sponsoringEmployerUen}
                      onChange={handleGrantFormChange}
                      borderColor={COLORS.maastrichtBlue}
                    />
                  </FormControl>
                </>
              )}

              <Button
                type="submit"
                bg={COLORS.mandarin}
                color="white"
                size="lg"
                isLoading={loading}
                _hover={{
                  bg: '#e06835',
                }}
                _active={{
                  bg: '#d55a29',
                }}
              >
                Preview JSON
              </Button>
            </VStack>
          </form>

          {jsonPreview && (
            <Box mt={8}>
              <Heading size="md" mb={4} color={COLORS.maastrichtBlue}>JSON Preview</Heading>
              <Textarea
                value={JSON.stringify(jsonPreview, null, 2)}
                isReadOnly
                rows={10}
                fontFamily="monospace"
                borderColor={COLORS.maastrichtBlue}
                bg="white"
                color="black"
                fontSize="sm"
              />
            </Box>
          )}

          <Button
            colorScheme="blue"
            onClick={testDirectAPI}
            isLoading={loading}
            mb={4}
          >
            Test Direct API Call (Structured)
          </Button>

          {apiResponse && (
            <Box mt={8}>
              <Divider mb={6} />
              <Heading size="md" mb={4} color={COLORS.maastrichtBlue}>
                API Response
                {apiResponse.status && (
                  <Badge 
                    ml={3} 
                    colorScheme={apiResponse.status >= 400 ? "red" : "green"}
                    fontSize="sm"
                  >
                    {apiResponse.status} {apiResponse.statusText}
                  </Badge>
                )}
              </Heading>

              <VStack spacing={4} align="stretch">
                {apiResponse.url && (
                  <Box>
                    <Text fontWeight="bold" color={COLORS.maastrichtBlue} mb={2}>
                      Request URL:
                    </Text>
                    <Text fontSize="sm" fontFamily="monospace" bg="gray.50" p={2} borderRadius="md">
                      {apiResponse.url}
                    </Text>
                  </Box>
                )}

                {apiResponse.headers && (
                  <Box>
                    <Text fontWeight="bold" color={COLORS.maastrichtBlue} mb={2}>
                      Response Headers:
                    </Text>
                    <Textarea
                      value={Object.entries(apiResponse.headers)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join('\n')}
                      isReadOnly
                      rows={4}
                      fontFamily="monospace"
                      fontSize="sm"
                      borderColor={COLORS.maastrichtBlue}
                      bg="white"
                      color="black"
                    />
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" color={COLORS.maastrichtBlue} mb={2}>
                    Response Body:
                  </Text>
                  <Textarea
                    value={typeof apiResponse.data === 'object' 
                      ? JSON.stringify(apiResponse.data, null, 2) 
                      : apiResponse.data
                    }
                    isReadOnly
                    rows={12}
                    fontFamily="monospace"
                    fontSize="sm"
                    borderColor={COLORS.maastrichtBlue}
                    bg="white"
                    color="black"
                  />
                </Box>

                {apiResponse.error && (
                  <Box>
                    <Text fontWeight="bold" color="red.500" mb={2}>
                      Error:
                    </Text>
                    <Text color="red.500" fontFamily="monospace">
                      {apiResponse.error}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    </Container>
  );
};

export default SSGAPI; 